// ResearchPage.jsx -- Selma's Workspace
// Selma is the intelligence analyst: researches accounts, finds executives,
// monitors competitors, enriches existing records, and fills the review queue
// that Jarvis will evaluate each morning.

import React, { useState, useEffect } from 'react';
import './ResearchPage.css';
import { useProfile } from '../context/UserProfileContext';
import { apiFetch } from '../lib/api';

// -- Selma avatar ----------------------------------------------
function SelmaAvatar({ size = 40, pulse = false }) {
  return (
    <div
      className={`selma-avatar${pulse ? ' selma-avatar--pulse' : ''}`}
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      <span style={{ fontSize: size * 0.5 }}>🔭</span>
    </div>
  );
}

// -- Selma speech bubble ---------------------------------------
function SelmaSays({ message, sub }) {
  const { researchName } = useProfile();
  return (
    <div className="selma-says">
      <SelmaAvatar size={36} />
      <div className="selma-says-body">
        <span className="selma-says-name">{researchName}</span>
        <p className="selma-says-msg">{message}</p>
        {sub && <p className="selma-says-sub">{sub}</p>}
      </div>
    </div>
  );
}

// -- Page header -----------------------------------------------
function SelmaHeader({ activeTab, onTabChange, selmaStatus }) {
  const { researchName } = useProfile();
  const tabs = [
    { id: 'quick',     icon: '⚡', label: 'Quick Research' },
    { id: 'assign',    icon: '📋', label: 'Assign Work' },
    { id: 'queue',     icon: '⏳', label: 'Active Queue' },
    { id: 'overnight', icon: '🌙', label: "Tonight's Batch" },
    { id: 'review',    icon: '📬', label: 'Review Queue' },
    { id: 'ingest',    icon: '📥', label: 'Data Ingestion' },
  ];
  return (
    <div className="selma-header">
      {/* Compact white header — matches Victor/Cyrano pattern */}
      <div className="selma-page-header">
        <div className="selma-page-header-left">
          {/* 🔍 — the same glyph as this page's AI-strip button. */}
          <span className="dm-page-mark dm-page-mark--selma">🔍</span>
          <div>
            <div className="selma-page-title">{researchName} Intelligence Studio</div>
            <div className="selma-page-sub">Account research and signal intelligence</div>
          </div>
        </div>
        <div className="selma-page-header-right">
          <div className={`selma-online-badge selma-online-badge--${(selmaStatus || 'standby').replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="selma-online-dot" />
            {selmaStatus || 'Standing By'}
          </div>
        </div>
      </div>
      {/* White tab bar — matches Victor/Cyrano */}
      <div className="selma-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`selma-tab${activeTab === t.id ? ' selma-tab--active' : ''}`}
            onClick={() => onTabChange(t.id)}
          >
            <span className="selma-tab-icon">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// TAB 1: QUICK RESEARCH (existing single-company flow)
// -------------------------------------------------------------
function Badge({ label, value, editing, options, onChange, colorMap }) {
  const color = colorMap[value] || 'blue';
  if (editing) return (
    <select className={`rp-badge-select rp-badge-select--${color}`} value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
  return <span className={`rp-badge rp-badge--${color}`}>{label ? `${label}: ` : ''}{value}</span>;
}

function EditableSection({ title, icon, content, onUpdate, isEditing, highlight = false }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`rp-section${highlight ? ' rp-section--highlight' : ''}`}>
      <button className="rp-section-hdr" onClick={() => setOpen(o => !o)}>
        <div className="rp-section-hdr-left">
          <span className="rp-section-ico">{icon}</span>
          <h3 className="rp-section-ttl">{title}</h3>
        </div>
        <svg className={`rp-chevron${open ? ' rp-chevron--open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="rp-section-body">
          {isEditing
            ? <textarea className="rp-textarea" value={content} onChange={e => onUpdate(e.target.value)} rows={8} />
            : <p className="rp-section-text">{content}</p>
          }
        </div>
      )}
    </div>
  );
}

function SuccessCard({ icon, title, item, contacts }) {
  return (
    <div className="rp-success-card">
      <div className="rp-success-icon">{icon}</div>
      <h3 className="rp-success-title">{title}</h3>
      {contacts ? (
        contacts.length > 0
          ? contacts.map((c, i) => (
            <div key={i} className="rp-success-row">
              <span className="rp-success-name">{c.name}</span>
              <span className="rp-success-action">{c.action === 'created' ? 'Created' : 'Updated'}</span>
            </div>
          ))
          : <p className="rp-success-empty">No contacts created</p>
      ) : (
        <>
          <p className="rp-success-name">{item?.name || item?.title}</p>
          <p className="rp-success-action">{item?.action === 'created' ? 'Created' : 'Updated'}</p>
          {item?.url && (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="rp-success-link">
              View {title} →
            </a>
          )}
        </>
      )}
    </div>
  );
}

function QuickResearchTab({ onNavigate, onStatusChange }) {
  const { greeting: getGreeting } = useProfile();
  const [companyName, setCompanyName]       = useState('');
  const [isResearching, setIsResearching]   = useState(false);
  const [isProcessing, setIsProcessing]     = useState(false);
  // B45 — the queued-confirmation message. Replaces the progress bar, which was
  // driven by a timer rather than by work and froze at 95% for minutes.
  const [queueNotice, setQueueNotice]       = useState('');
  const [researchResults, setResearchResults] = useState(null);
  const [error, setError]                   = useState(null);
  const [isEditing, setIsEditing]           = useState(false);
  const [editedData, setEditedData]         = useState(null);
  const [processResults, setProcessResults] = useState(null);
  const [greeting, setGreeting]             = useState('');
  const [pastSessions, setPastSessions]     = useState([]);
  const [checkingMemory, setCheckingMemory] = useState(false);

  useEffect(() => { setGreeting(getGreeting()); }, [getGreeting]);

  // Check Selma's memory when company name is entered
  const checkMemory = async (name) => {
    if (!name || name.length < 3) { setPastSessions([]); return; }
    setCheckingMemory(true);
    try {
      const res = await apiFetch('/api/selma/past-sessions?companyName=' + encodeURIComponent(name));
      const data = await res.json();
      setPastSessions(data.sessions || []);
    } catch (_) { setPastSessions([]); }
    finally { setCheckingMemory(false); }
  };

  // B45 — queue the research instead of holding the browser open for it.
  //
  // This used to POST /api/research/analyze and await the whole thing: one
  // non-streaming Claude call over ~15 searches' worth of context, measured at
  // 80-150s with a 240s ceiling. Meanwhile the progress bar below was driven by a
  // setInterval, not by work — it hit 95% at 38 seconds and then froze for up to
  // four more minutes. The 4 Sep logs caught nine attempts in one day: the same
  // person retrying a request that was still running.
  //
  // /api/research/start writes a research_queue row, returns 202 immediately, and
  // kicks the drain in the background. The queue list further down this page
  // already renders Pending / Running / Complete / Failed, so the state the user
  // needs is somewhere they can already see — and the finished report lands in
  // raw_intelligence, which the account pages and the brief read back.
  //
  // It also queues as 'Auto' rather than the old hardcoded 'full', which lets
  // run-batch pick the cheap refresh path (Haiku, ~15s) for an account that already
  // has signals. The browser was the only caller always paying for the full sweep.
  const handleResearch = async () => {
    if (!companyName.trim()) return;
    setIsResearching(true); setError(null); setResearchResults(null);
    onStatusChange?.('Online — Researching');
    setProcessResults(null); setEditedData(null); setIsEditing(false);
    try {
      const r = await apiFetch('/api/research/start', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountName: companyName }),
      });
      const data = await r.json();
      if (r.ok && data.success) {
        setQueueNotice(`${data.accountName} is queued. Research runs in the background — ` +
                       'open Active Queue to watch it, or carry on with something else.');
        setCompanyName('');
      } else {
        setError(data.error || 'Could not queue research. Please try again.');
      }
    } catch { setError('Failed to connect. Please try again.'); }
    finally { setIsResearching(false); onStatusChange?.('Intel Waiting'); }
  };

  const handleProcessIntel = async () => {
    setIsProcessing(true); setError(null);
    try {
      const { rawIntelligenceId, ...clean } = editedData;
      const r = await apiFetch('/api/process-intel', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rawIntelligenceId, directData: clean }),
      });
      const data = await r.json();
      if (data.account) { setProcessResults(data); setIsEditing(false); }
      else { setError(data.error || 'Failed to process intelligence'); }
    } catch { setError('Failed to connect to processing service'); }
    finally { setIsProcessing(false); }
  };

  const updateField = (f, v) => setEditedData(p => ({ ...p, [f]: v }));
  const addExec = () => setEditedData(p => ({ ...p, executives: [...p.executives, { name: '', title: '', background: '' }] }));
  const updateExec = (i, f, v) => setEditedData(p => ({ ...p, executives: p.executives.map((e, idx) => idx === i ? { ...e, [f]: v } : e) }));
  const removeExec = (i) => setEditedData(p => ({ ...p, executives: p.executives.filter((_, idx) => idx !== i) }));
  const reset = () => { setResearchResults(null); setEditedData(null); setProcessResults(null); setIsEditing(false); setError(null); setCompanyName(''); setPastSessions([]); };

  if (processResults) return (
    <div className="rp-body">
      <SelmaSays message={`${processResults.account?.name || companyName} has been added to your pipeline.`} sub="Account, contacts, and lead created." />
      <div className="rp-success-grid">
        <SuccessCard icon="🏢" title="Account" item={processResults.account} />
        <SuccessCard icon="👔" title="Contacts" contacts={processResults.contacts || []} />
        <SuccessCard icon="🎯" title="Lead" item={processResults.lead} />
      </div>
      <button className="rp-btn-reset" onClick={reset}>🔍 Research Another Company</button>
    </div>
  );

  return (
    <div className="rp-body">
      {!researchResults && !isResearching && (
        <div className="rp-search-block">
          <SelmaSays
            message={greeting}
            sub="Enter a carrier name for a full intelligence package -- leadership, technology, strategic signals, and opportunity scoring."
          />
          {/* Previously researched banner */}
          {pastSessions.length > 0 && (
            <div className="rp-memory-banner">
              <span className="rp-memory-icon">🧠</span>
              <div className="rp-memory-body">
                <span className="rp-memory-title">Selma has researched {companyName} before</span>
                <span className="rp-memory-sub">
                  Last researched: {pastSessions[0].date} — contacts and signals already on file will not be duplicated
                </span>
              </div>
            </div>
          )}
          {checkingMemory && (
            <div className="rp-memory-checking">🔍 Checking Selma's memory...</div>
          )}

          <div className="rp-search-row">
            <input
              className="rp-input"
              type="text"
              value={companyName}
              onChange={e => { setCompanyName(e.target.value); checkMemory(e.target.value); }}
              onKeyDown={e => e.key === 'Enter' && companyName.trim() && handleResearch()}
              placeholder="e.g. Grinnell Mutual, Donegal, ALFA Insurance..."
              autoFocus
            />
            <button className="rp-btn-research" onClick={handleResearch} disabled={!companyName.trim()}>
              Research →
            </button>
          </div>
          <div className="rp-capabilities">
            {['Executive profiles', 'Technology landscape', 'Strategic initiatives', 'Opportunity scoring', 'Platform signals', 'M&A activity'].map(c => (
              <span key={c} className="rp-cap-tag">✦ {c}</span>
            ))}
          </div>
        </div>
      )}

      {isResearching && (
        <div className="rp-progress-block">
          <SelmaAvatar size={80} pulse />
          <p className="rp-progress-msg">Queueing research…</p>
        </div>
      )}

      {/* B45 — the queued confirmation. The work itself is visible in Active Queue;
          this only has to say where it went. */}
      {queueNotice && !isResearching && (
        <div className="rp-progress-block">
          <SelmaAvatar size={80} />
          <p className="rp-progress-msg">{queueNotice}</p>
        </div>
      )}

      {error && (
        <div className="rp-error-bar">
          <span>⚠️ {error}</span>
          <button className="rp-btn-ghost-sm" onClick={() => setError(null)}>x</button>
        </div>
      )}

      {researchResults && editedData && (
        <div className="rp-results">
          <SelmaSays message={`Here's what I found on ${editedData.companyName}. Review and edit if needed, then process to save to your pipeline.`} />
          {researchResults.deltaSummary && (
            <div className={`rp-delta-banner rp-delta-${
              researchResults.deltaSummary.highPriorityCount > 0 ? 'action' :
              researchResults.deltaSummary.newSignalsCount > 0 ? 'new' : 'watch'
            }`}>
              <div className="rp-delta-icon">
                {researchResults.deltaSummary.highPriorityCount > 0 ? '🔴' :
                 researchResults.deltaSummary.newSignalsCount > 0 ? '🟡' : '🟢'}
              </div>
              <div className="rp-delta-body">
                <div className="rp-delta-title">{researchResults.deltaSummary.recommendation}</div>
                <div className="rp-delta-detail">
                  {researchResults.deltaSummary.newSignalsCount} new signal{researchResults.deltaSummary.newSignalsCount !== 1 ? 's' : ''} · {researchResults.deltaSummary.executivesFound} executive{researchResults.deltaSummary.executivesFound !== 1 ? 's' : ''} found · vs last research: {researchResults.deltaSummary.lastResearchDate}
                </div>
                {researchResults.deltaSummary.newSignals?.length > 0 && (
                  <div className="rp-delta-signals">{researchResults.deltaSummary.newSignals.join(' · ')}</div>
                )}
              </div>
            </div>
          )}
          <div className="rp-action-bar">
            <div className="rp-action-bar-left">
              <button className={`rp-btn-edit${isEditing ? ' rp-btn-edit--on' : ''}`} onClick={() => setIsEditing(e => !e)}>
                {isEditing ? 'Editing' : 'Edit'}
              </button>
              <button className="rp-btn-process" onClick={handleProcessIntel} disabled={isProcessing || isEditing}>
                {isProcessing ? 'Processing...' : 'Process Intel'}
              </button>
            </div>
            <button className="rp-btn-ghost-sm" onClick={reset}>New Research</button>
          </div>
          <div className="rp-company-hdr">
            {isEditing
              ? <input className="rp-company-name-input" value={editedData.companyName} onChange={e => updateField('companyName', e.target.value)} />
              : <h2 className="rp-company-name">{editedData.companyName}</h2>
            }
            <div className="rp-badges">
              <Badge label="Priority" value={editedData.priority} editing={isEditing} options={['High','Medium','Low']} onChange={v => updateField('priority', v)} colorMap={{ High:'red', Medium:'blue', Low:'gray' }} />
              <Badge label="Deal" value={editedData.dealSize} editing={isEditing} options={['$100K-$300K','$300K-$600K','$600K-$1M','$1M+']} onChange={v => updateField('dealSize', v)} colorMap={{}} />
              <Badge label="" value={editedData.opportunityStatus} editing={isEditing} options={['Under Review','Pursue','Archive']} onChange={v => updateField('opportunityStatus', v)} colorMap={{ Pursue:'green', 'Under Review':'blue', Archive:'gray' }} />
            </div>
          </div>
          <div className="rp-sections">
            <EditableSection title="Company Overview" icon="🏢" content={editedData.companyOverview} onUpdate={v => updateField('companyOverview', v)} isEditing={isEditing} />
            <div className="rp-section">
              <div className="rp-section-hdr" style={{ cursor: 'default' }}>
                <div className="rp-section-hdr-left">
                  <span className="rp-section-ico">👔</span>
                  <h3 className="rp-section-ttl">Executive Contacts</h3>
                </div>
                {isEditing && <button className="rp-btn-add-exec" onClick={addExec}>+ Add</button>}
              </div>
              <div className="rp-section-body">
                {editedData.executives.length === 0
                  ? <p className="rp-empty-note">No executives identified</p>
                  : editedData.executives.map((ex, i) => (
                    <div key={i} className="rp-exec-card">
                      {isEditing ? (
                        <div className="rp-exec-edit-grid">
                          <input className="rp-exec-inp" value={ex.name} onChange={e => updateExec(i,'name',e.target.value)} placeholder="Name" />
                          <input className="rp-exec-inp" value={ex.title} onChange={e => updateExec(i,'title',e.target.value)} placeholder="Title" />
                          <button className="rp-btn-rm-exec" onClick={() => removeExec(i)}>x</button>
                          <textarea className="rp-exec-bio-inp" value={ex.background} onChange={e => updateExec(i,'background',e.target.value)} placeholder="Background..." rows={2} />
                        </div>
                      ) : (
                        <>
                          <div className="rp-exec-name">{ex.name} <span className="rp-exec-ttl">· {ex.title}</span></div>
                          {ex.background && <p className="rp-exec-bio">{ex.background}</p>}
                        </>
                      )}
                    </div>
                  ))
                }
              </div>
            </div>
            <EditableSection title="Technology Landscape" icon="💻" content={editedData.technologyLandscape} onUpdate={v => updateField('technologyLandscape', v)} isEditing={isEditing} />
            <EditableSection title="Strategic Initiatives" icon="🎯" content={editedData.strategicInitiatives} onUpdate={v => updateField('strategicInitiatives', v)} isEditing={isEditing} />
            <EditableSection title="Opportunity Analysis" icon="💡" content={editedData.opportunityAnalysis} onUpdate={v => updateField('opportunityAnalysis', v)} isEditing={isEditing} highlight />
            <EditableSection title="Recommended Next Actions" icon="✅" content={editedData.nextActions} onUpdate={v => updateField('nextActions', v)} isEditing={isEditing} highlight />
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// TAB 2: ASSIGN WORK
// -----------------------------------------------------------------
const JOB_TYPES = [
  {
    id: 'new_account',
    icon: '🏢',
    label: 'New Account Research',
    color: 'indigo',
    desc: 'Companies not yet in your system. Selma builds a full intelligence package from scratch and queues for review.',
    placeholder: 'Enter company names, one per line...\ne.g.\nGrinnell Mutual\nDonegal Insurance\nALFA Insurance Group',
    allowBatch: true,
  },
  {
    id: 'enrichment',
    icon: '🔄',
    label: 'Data Enrichment & Update',
    color: 'blue',
    desc: 'Refresh existing accounts with new signals, updated leadership, and revised opportunity scores.',
    placeholder: 'Search existing accounts...',
    allowBatch: true,
  },
  {
    id: 'exec_search',
    icon: '👔',
    label: 'Executive Search',
    color: 'purple',
    desc: 'Find specific executives by title, company, industry, or geography. Powered by ZoomInfo.',
    allowBatch: false,
  },
  {
    id: 'competitor',
    icon: '🏁',
    label: 'Competitor Intelligence',
    color: 'red',
    desc: 'Deep competitive brief -- positioning, recent moves, target accounts, win/loss signals.',
    placeholder: 'Enter competitor names, one per line...',
    allowBatch: true,
  },
  {
    id: 'vendor',
    icon: '🔧',
    label: 'Vendor Research',
    color: 'amber',
    desc: 'Vendor profiles -- products, pricing signals, integration landscape, partnership opportunities.',
    placeholder: 'Enter vendor names, one per line...',
    allowBatch: true,
  },
];

function AssignWorkTab({ onJobQueued }) {
  const { firstName } = useProfile();
  const [selected, setSelected] = useState(null);
  const [inputText, setInputText] = useState('');
  const [runNow, setRunNow] = useState(false);
  // industry starts EMPTY, not 'P&C Insurance'. A pre-filled default is not a
  // hint: it is a search the user did not ask for, and it runs unless they
  // notice and clear it — so every tenant prospected one market by default. The
  // field is free text and the placeholder carries the hint instead.
  const [execCriteria, setExecCriteria] = useState({ title: '', industry: '', geography: '', companySize: '', companyName: '', mode: 'prospecting' });
  const [queued, setQueued] = useState(false);
  const [queueError, setQueueError] = useState(null);
  const [execRunning, setExecRunning] = useState(false);
  const [execResult, setExecResult] = useState(null);
  const [accountsList, setAccountsList] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');

  // Fetch accounts for enrichment dropdown
  useEffect(() => {
    if (selected?.id === 'enrichment' && accountsList.length === 0) {
      apiFetch('/api/accounts/list')
        .then(r => r.json())
        .then(data => {
          if (data.accounts) {
            setAccountsList(data.accounts.sort((a, b) => (a.accountName || '').localeCompare(b.accountName || '')));
          }
        })
        .catch(() => {});
    }
  }, [selected?.id, accountsList.length]);

  const toggleAccount = (account) => {
    setSelectedAccounts(prev =>
      prev.find(a => a.id === account.id)
        ? prev.filter(a => a.id !== account.id)
        : [...prev, account]
    );
  };
  const [execError, setExecError] = useState(null);

  // Executive search runs immediately via API -- ZoomInfo -> Notion Contacts
  const handleExecSearch = async () => {
    setExecRunning(true);
    setExecResult(null);
    setExecError(null);
    try {
      const mode = execCriteria.companyName.trim() ? 'buying_committee' : 'prospecting';
      const res = await apiFetch('/api/research/exec-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          companyName: execCriteria.companyName || undefined,
          titles: execCriteria.title.split(',').map(t => t.trim()).filter(Boolean),
          industry: execCriteria.industry,
          geography: execCriteria.geography,
          companySize: execCriteria.companySize,
          maxResults: 25,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setExecResult(data);
      } else {
        setExecError(data.error || 'Search failed');
      }
    } catch (err) {
      setExecError('Could not connect to search service');
    } finally {
      setExecRunning(false);
    }
  };

  const handleQueue = async () => {
    if (!selected) return;

    const targets = selected.id === 'enrichment'
      ? selectedAccounts.map(a => ({ accountId: a.id, accountName: a.accountName }))
      : inputText.split('\n').map(s => s.trim()).filter(Boolean).map(name => ({ accountName: name }));

    if (targets.length === 0) return;

    setQueued(false);
    setQueueError(null);

    try {
      if (runNow) {
        // ⚡ Run Now:
        // Queue as Pending then fire run-batch — it handles Pending→Running→Complete
        // and returns 200 immediately while processing in the background.
        // This is identical to the overnight path but triggers immediately.
        await apiFetch('/api/research/queue', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accounts: targets, queuedBy: firstName || 'user' })
        });
        // Warm the runtime before triggering batch to avoid cold-start timeout
        try { await apiFetch('/api/research/warm'); } catch {}
        await new Promise(r => setTimeout(r, 800));
        await apiFetch('/api/research/run-batch', { method: 'POST' });
        onJobQueued && onJobQueued({ type: selected.id, targets, runNow });
      } else {
        // 🌙 Tonight's batch — queue to Notion Research Queue DB as Pending
        const res = await apiFetch('/api/research/queue', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accounts: targets, queuedBy: firstName || 'user' })
        });
        await res.json();
      }

      setQueued(true);
      onJobQueued && onJobQueued({ type: selected.id, targets, runNow });
      setTimeout(() => {
        setQueued(false);
        setSelected(null);
        setInputText('');
        setSelectedAccounts([]);
        setAccountSearch('');
      }, 2500);
    } catch (err) {
      console.error('Queue error:', err);
      setQueueError('Something went wrong: ' + err.message);
    }
  };

  const canSubmit = selected && (
    selected.id === 'exec_search'
      ? execCriteria.title.trim()
      : selected.id === 'enrichment'
      ? selectedAccounts.length > 0
      : inputText.trim().length > 0
  );

  return (
    <div className="rp-body">
      <SelmaSays
        message={`What would you like me to work on?`}
        sub="Pick a job type, enter your targets, and I'll get started -- either right now or overnight."
      />

      {queued && (
        <div className="selma-queued-banner">
          ✓ Job queued successfully. Check the {runNow ? 'Active Queue' : "Tonight's Batch"} tab.
        </div>
      )}
      {queueError && (
        <div className="selma-queue-error">
          ⚠️ {queueError}
        </div>
      )}

      {/* Job type cards */}
      <div className="assign-job-grid">
        {JOB_TYPES.map(jt => (
          <button
            key={jt.id}
            className={`assign-job-card assign-job-card--${jt.color}${selected?.id === jt.id ? ' assign-job-card--selected' : ''}`}
            onClick={() => { setSelected(jt); setInputText(''); }}
          >
            <span className="assign-job-icon">{jt.icon}</span>
            <span className="assign-job-label">{jt.label}</span>
            <p className="assign-job-desc">{jt.desc}</p>
          </button>
        ))}
      </div>

      {/* Input panel */}
      {selected && (
        <div className="assign-input-panel">
          <div className="assign-input-header">
            <span className="assign-input-title">{selected.icon} {selected.label}</span>
          </div>

          {selected.id === 'exec_search' ? (
            <div className="exec-search-form">
              <div className="exec-search-mode-toggle">
                <button
                  className={`exec-mode-btn${!execCriteria.companyName ? ' exec-mode-btn--active' : ''}`}
                  onClick={() => setExecCriteria(p => ({ ...p, companyName: '' }))}
                  type="button"
                >
                  🔍 Prospecting Search
                </button>
                <button
                  className={`exec-mode-btn${execCriteria.companyName !== undefined && execCriteria._buyingCommittee ? ' exec-mode-btn--active' : ''}`}
                  onClick={() => setExecCriteria(p => ({ ...p, _buyingCommittee: true }))}
                  type="button"
                >
                  🏢 Buying Committee
                </button>
              </div>
              {execCriteria._buyingCommittee && (
                <div className="exec-search-row">
                  <label className="exec-search-label">Company Name *</label>
                  <input
                    className="rp-input"
                    value={execCriteria.companyName}
                    onChange={e => setExecCriteria(p => ({ ...p, companyName: e.target.value }))}
                    placeholder="e.g. Donegal Insurance Group"
                  />
                </div>
              )}
              <div className="exec-search-row">
                <label className="exec-search-label">Titles / Roles * <span className="exec-search-hint">(comma separated)</span></label>
                <input
                  className="rp-input"
                  value={execCriteria.title}
                  onChange={e => setExecCriteria(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. CIO, CTO, VP of Claims, VP of IT"
                />
              </div>
              <div className="exec-search-row">
                <label className="exec-search-label">Industry</label>
                <input
                  className="rp-input"
                  value={execCriteria.industry}
                  onChange={e => setExecCriteria(p => ({ ...p, industry: e.target.value }))}
                  placeholder="e.g. the market you sell into"
                />
              </div>
              {!execCriteria._buyingCommittee && (
                <>
                  <div className="exec-search-row">
                    <label className="exec-search-label">Geography</label>
                    <input
                      className="rp-input"
                      value={execCriteria.geography}
                      onChange={e => setExecCriteria(p => ({ ...p, geography: e.target.value }))}
                      placeholder="e.g. Midwest, Ohio, United States"
                    />
                  </div>
                  <div className="exec-search-row">
                    <label className="exec-search-label">Company Size</label>
                    <select className="rp-input" value={execCriteria.companySize} onChange={e => setExecCriteria(p => ({ ...p, companySize: e.target.value }))}>
                      <option value="">Any size</option>
                      <option value="1-500M">Under $500M written premium</option>
                      <option value="500M-2B">$500M - $2B written premium</option>
                      <option value="2B+">Over $2B written premium</option>
                    </select>
                  </div>
                </>
              )}
              {execError && <div className="rp-error-bar"><span>⚠️ {execError}</span></div>}
              {execResult && (
                <div className="exec-result-banner">
                  ✓ {execResult.message}
                  {execResult.contacts?.filter(c => c.status === 'created').map((c, i) => (
                    <div key={i} className="exec-result-row">
                      <span className="exec-result-name">{c.name}</span>
                      <span className="exec-result-title">{c.title}</span>
                      <span className="exec-result-co">{c.company}</span>
                      {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" className="exec-result-link">View →</a>}
                    </div>
                  ))}
                </div>
              )}
              <div className="assign-actions">
                <button className="rp-btn-ghost-sm" onClick={() => setSelected(null)}>Cancel</button>
                <button
                  className="rp-btn-process"
                  onClick={handleExecSearch}
                  disabled={execRunning || !execCriteria.title.trim()}
                >
                  {execRunning ? 'Searching ZoomInfo...' : 'Search & Save to Contacts →'}
                </button>
              </div>
            </div>
          ) : selected.id === 'enrichment' ? (
            <div className="assign-account-picker">
              <input
                className="assign-account-search"
                placeholder="Search accounts..."
                value={accountSearch}
                onChange={e => setAccountSearch(e.target.value)}
              />
              {selectedAccounts.length > 0 && (
                <div className="assign-account-selected-row">
                  {selectedAccounts.map(a => (
                    <span key={a.id} className="assign-account-chip">
                      {a.accountName}
                      <button onClick={() => toggleAccount(a)} type="button">×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="assign-account-list">
                {accountsList
                  .filter(a => !accountSearch || a.accountName?.toLowerCase().includes(accountSearch.toLowerCase()))
                  .filter(a => !selectedAccounts.find(s => s.id === a.id))
                  .slice(0, 20)
                  .map(a => (
                    <div key={a.id} className="assign-account-option" onClick={() => toggleAccount(a)}>
                      <span className="assign-account-name">{a.accountName}</span>
                      {a.tier && <span className="assign-account-tier">{a.tier}</span>}
                    </div>
                  ))
                }
                {accountsList.length === 0 && <div className="assign-account-loading">Loading accounts...</div>}
              </div>
              <div className="assign-count">{selectedAccounts.length} account{selectedAccounts.length !== 1 ? 's' : ''} selected</div>
            </div>
          ) : (
            <div className="assign-textarea-wrap">
              <textarea
                className="assign-textarea"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                placeholder={selected.placeholder}
                rows={6}
                autoFocus
              />
              <div className="assign-count">
                {inputText.split('\n').filter(s => s.trim()).length} {selected.allowBatch ? 'targets' : 'target'}
              </div>
            </div>
          )}

          <div className="assign-run-options">
            <label className="assign-run-option">
              <input type="radio" checked={!runNow} onChange={() => setRunNow(false)} />
              <span>
                <strong>🌙 Add to tonight's batch</strong>
                <span className="assign-run-desc">Runs overnight, results ready when you log in tomorrow</span>
              </span>
            </label>
            <label className="assign-run-option">
              <input type="radio" checked={runNow} onChange={() => setRunNow(true)} />
              <span>
                <strong>⚡ Run now</strong>
                <span className="assign-run-desc">Starts immediately, results appear in Active Queue</span>
              </span>
            </label>
          </div>

          <div className="assign-actions">
            <button className="rp-btn-ghost-sm" onClick={() => setSelected(null)}>Cancel</button>
            <button className="rp-btn-process" onClick={handleQueue} disabled={!canSubmit}>
              {runNow ? 'Start Now →' : 'Queue for Tonight →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// TAB 3: ACTIVE QUEUE
// -----------------------------------------------------------------
function ActiveQueueTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState(null);

  const loadQueue = () => {
    Promise.all([
      apiFetch('/api/research/queue?status=Pending').then(r => r.json()),
      apiFetch('/api/research/queue?status=Running').then(r => r.json()),
      apiFetch('/api/research/queue?status=Failed').then(r => r.json()),
      apiFetch('/api/research/queue?status=Complete').then(r => r.json()),
    ]).then(([pending, running, failed, complete]) => {
      const now = Date.now();
      const recent = (complete.items || []).filter(i => {
        if (!i.completedAt) return false;
        return (now - new Date(i.completedAt).getTime()) < 30 * 60 * 1000;
      });
      setItems([
        ...(pending.items  || []),
        ...(running.items  || []),
        ...(failed.items   || []),
        ...recent,
      ]);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    loadQueue();
    const interval = setInterval(loadQueue, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleRunNow = async () => {
    setTriggering(true);
    setTriggerMsg('⏳ Warming up...');
    try {
      // Warm the runtime first
      try { await apiFetch('/api/research/warm'); } catch {}
      await new Promise(r => setTimeout(r, 800));

      // Count pending items
      const pendingCount = pending.length;
      if (pendingCount === 0) {
        setTriggerMsg('✅ No pending jobs — queue is empty.');
        return;
      }

      // Call run-batch once per pending account — each call processes ONE account
      // We await each call so they run sequentially, not simultaneously
      setTriggerMsg(`⏳ Starting ${pendingCount} job${pendingCount > 1 ? 's' : ''}...`);
      let completed = 0;
      for (let i = 0; i < pendingCount; i++) {
        setTriggerMsg(`⚙️ Researching ${i + 1}/${pendingCount}${i === 0 ? '' : ` (${completed} done)`}...`);
        const res  = await apiFetch('/api/research/run-batch', { method: 'POST' });
        const data = await res.json();
        if (data.success) {
          completed++;
          setTriggerMsg(`✅ ${completed}/${pendingCount} complete: ${data.account || ''}`);
        } else {
          setTriggerMsg(`⚠️ Job ${i + 1} issue: ${data.message || 'unknown'} — continuing...`);
        }
        // Refresh queue after each account so user sees progress
        loadQueue();
      }

      setTriggerMsg(`✅ Batch complete — ${completed}/${pendingCount} succeeded.`);
      setTimeout(loadQueue, 2000);
    } catch (err) {
      setTriggerMsg('⚠️ Could not reach batch runner: ' + err.message);
    } finally {
      setTriggering(false);
    }
  };

  const handleResetStuck = async () => {
    try {
      await apiFetch('/api/research/queue-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetAll: true }),
      });
      setTriggerMsg('✅ Stuck jobs reset to Pending.');
      setTimeout(loadQueue, 1000);
    } catch (err) {
      setTriggerMsg('⚠️ Reset failed: ' + err.message);
    }
  };

  const pending = items.filter(i => i.status === 'Pending');
  const running = items.filter(i => i.status === 'Running');
  const failed  = items.filter(i => i.status === 'Failed');
  const done    = items.filter(i => i.status === 'Complete');

  const statusEmoji = { Pending: '⏳', Running: '⚙️', Failed: '❌', Complete: '✅' };

  const selmaMsg = loading ? 'Checking active jobs...'
    : running.length > 0 ? `Working on ${running.length} job${running.length > 1 ? 's' : ''}...`
    : pending.length > 0 ? `${pending.length} job${pending.length > 1 ? 's' : ''} queued and ready — click Run Now to start.`
    : failed.length > 0 ? `${failed.length} job${failed.length > 1 ? 's' : ''} failed — check details below.`
    : done.length > 0 ? 'All recent jobs complete.'
    : 'No active jobs right now.';

  return (
    <div className="rp-body">
      <SelmaSays
        message={selmaMsg}
        sub={!loading && items.length === 0 ? "Assign work from the 'Assign Work' tab to get started." : undefined}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {pending.length > 0 && (
          <button className="rp-btn-primary-sm" onClick={handleRunNow} disabled={triggering}>
            {triggering ? '⏳ Starting...' : `⚡ Run Now (${pending.length} pending)`}
          </button>
        )}
        {running.length > 0 && (
          <button className="rp-btn-ghost-sm" onClick={handleResetStuck}>
            🔄 Reset Stuck Jobs
          </button>
        )}
        <button className="rp-btn-ghost-sm" onClick={loadQueue}>↻ Refresh</button>
      </div>

      {triggerMsg && (
        <div style={{ fontSize: 13, color: 'var(--dm-grey-800)', background: '#f3f4f6', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
          {triggerMsg}
        </div>
      )}

      {!loading && items.length === 0 ? (
        <div className="selma-empty-state">
          <span className="selma-empty-icon">⏳</span>
          <p>No jobs running. Go to Assign Work to give me something to do.</p>
        </div>
      ) : (
        <div className="selma-job-list">
          {items.map(item => (
            <div key={item.id} className={`selma-queue-row selma-queue-row--${(item.status||'').toLowerCase()}`}>
              <span className="selma-queue-emoji">{statusEmoji[item.status] || '⏳'}</span>
              <span className="selma-queue-name">{item.accountName}</span>
              <span className={`selma-queue-status selma-queue-status--${(item.status||'').toLowerCase()}`}>{item.status}</span>
              {item.startedAt && <span className="selma-queue-time">Started {new Date(item.startedAt).toLocaleTimeString()}</span>}
              {item.completedAt && <span className="selma-queue-time">Done {new Date(item.completedAt).toLocaleTimeString()}</span>}
              {/* P0.32 32.7 — WHERE A WAITING REQUEST ACTUALLY IS.
                  Measured over 30 days on 24 Oct 2026, the median time from
                  queueing to STARTING was 184 hours — nearly eight days — and
                  the row said "Pending" on day one and "Pending" on day eight.
                  One tenant's oldest request had been waiting 142 hours.

                  `queuePosition` is counted across the tenant's WHOLE queue, so
                  "3 of 42" cannot be misread as "nearly next". Both are null
                  when unknown rather than guessed — a position of 1 on a failed
                  count would read as "yours is next". */}
              {item.status === 'Pending' && item.queuePosition && item.queueTotal && (
                <span className="selma-queue-position">
                  {item.queuePosition} of {item.queueTotal} waiting
                </span>
              )}
              {item.status === 'Pending' && item.ageHours != null && (
                <span className={'selma-queue-age'
                  + (item.ageHours >= 48 ? ' selma-queue-age--stale' : '')}>
                  {item.ageHours < 1
                    ? 'queued just now'
                    : item.ageHours < 48
                      ? `waiting ${Math.round(item.ageHours)}h`
                      : `waiting ${Math.round(item.ageHours / 24)} days`}
                </span>
              )}
              {/* `notes` IS NOT A COLUMN on research_queue — verified against the
                  live schema in P0.30 — so this rendered the empty string on
                  every row. Replaced rather than deleted silently, because the
                  space it occupied is exactly where the queue state belongs. */}
              {item.status === 'Failed' && (
                <button
                  className="selma-delete-btn"
                  onClick={async () => {
                    try {
                      await apiFetch('/api/research/queue-reset', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: item.id, status: 'Cancelled' }),
                      });
                      setTimeout(loadQueue, 500);
                    } catch {}
                  }}
                  title="Dismiss failed job"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// TAB 4: TONIGHT'S BATCH
// -----------------------------------------------------------------
function OvernightBatchTab() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [deleting, setDeleting]   = useState({});
  const [toast, setToast]         = useState('');

  const loadQueue = () => {
    setLoading(true);
    apiFetch('/api/research/queue?status=Pending')
      .then(r => r.json())
      .then(data => { setItems(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadQueue(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const handleDelete = async (item) => {
    if (confirmDeleteId !== item.id) { setConfirmDeleteId(item.id); return; }
    setDeleting(prev => ({ ...prev, [item.id]: true }));
    try {
      const res  = await apiFetch('/api/research/queue-manage', {
        method:  'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: item.id }),
      });
      const data = await res.json();
      if (data.success) {
        setItems(prev => prev.filter(i => i.id !== item.id));
        showToast(`✅ "${item.accountName}" removed from queue`);
      } else {
        showToast(`❌ ${data.error || 'Failed to remove'}`);
      }
    } catch {
      showToast('❌ Error removing item');
    } finally {
      setDeleting(prev => ({ ...prev, [item.id]: false }));
      setConfirmDeleteId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (items.length === 0) return;
    if (!confirmDeleteAll) { setConfirmDeleteAll(true); return; }
    setConfirmDeleteAll(false);
    for (const item of items) {
      try {
        await apiFetch('/api/research/queue-manage', {
          method:  'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: item.id }),
        });
      } catch { /* continue */ }
    }
    setItems([]);
    showToast(`✅ All ${items.length} jobs cleared from queue`);
  };

  return (
    <div className="rp-body">
      {toast && <div className="rp-toast">{toast}</div>}
      <SelmaSays
        message={loading ? 'Loading batch queue...' : items.length > 0 ? `I have ${items.length} job(s) queued for tonight's batch run at 2 AM.` : "Tonight's batch is empty."}
        sub={!loading && items.length === 0 ? "Add jobs from the 'Assign Work' tab before you log off tonight." : undefined}
      />
      {!loading && items.length > 0 && (
        <div className="overnight-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="overnight-count">{items.length} jobs scheduled · Runs at 2:00 AM UTC</span>
          {confirmDeleteAll ? (
            <>
              <button className="rp-btn-danger-sm" onClick={handleDeleteAll}>Confirm clear all?</button>
              <button className="rp-btn-ghost-sm" onClick={() => setConfirmDeleteAll(false)}>Cancel</button>
            </>
          ) : (
            <button className="rp-btn-danger-sm" onClick={handleDeleteAll} title="Clear entire queue">🗑 Clear All</button>
          )}
        </div>
      )}
      {!loading && items.length === 0 ? (
        <div className="selma-empty-state">
          <span className="selma-empty-icon">🌙</span>
          <p>Nothing scheduled yet. Add jobs before you log off and I'll handle them overnight.</p>
        </div>
      ) : (
        <div className="selma-job-list">
          {items.map(item => (
            <div key={item.id} className="selma-queue-row" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="selma-queue-name" style={{ flex: 1 }}>{item.accountName}</span>
              <span className="selma-queue-status selma-queue-status--pending">{item.status}</span>
              {item.queuedAt && <span className="selma-queue-time">Queued {new Date(item.queuedAt).toLocaleTimeString()}</span>}
              {confirmDeleteId === item.id ? (
                <>
                  <button className="rp-btn-icon-delete rp-btn-confirm" onClick={() => handleDelete(item)} disabled={deleting[item.id]}>Remove?</button>
                  <button className="rp-btn-icon-cancel" onClick={() => setConfirmDeleteId(null)}>✕</button>
                </>
              ) : (
                <button className="rp-btn-icon-delete" onClick={() => handleDelete(item)} disabled={deleting[item.id]} title="Remove from queue">
                  {deleting[item.id] ? '⏳' : '✕'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!loading && <button className="rp-btn-ghost-sm" style={{marginTop:12}} onClick={loadQueue}>↻ Refresh</button>}
    </div>
  );
}

// -----------------------------------------------------------------
// TAB 5: REVIEW QUEUE
// -----------------------------------------------------------------
function ReviewQueueTab() {
  const { commandName } = useProfile();
  const [items, setItems] = useState([]);
  const [acting, setActing] = useState({}); // itemId → 'approving'|'archiving'|'done'|'error'

  useEffect(() => {
    apiFetch('/api/research/queue?status=Complete')
      .then(r => r.json())
      .then(data => { setItems(data.items || []); })
      .catch(() => {});
  }, []);

  const handleApprove = async (item) => {
    setActing(prev => ({ ...prev, [item.id]: 'approving' }));
    try {
      const res = await apiFetch('/api/research/queue', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, status: 'Reviewed' }),
      });
      const data = await res.json();
      if (data.success) {
        setActing(prev => ({ ...prev, [item.id]: 'done' }));
        setItems(prev => prev.filter(i => i.id !== item.id));
      } else {
        setActing(prev => ({ ...prev, [item.id]: 'error' }));
      }
    } catch {
      setActing(prev => ({ ...prev, [item.id]: 'error' }));
    }
  };

  const handleArchive = async (item) => {
    setActing(prev => ({ ...prev, [item.id]: 'archiving' }));
    try {
      const res = await apiFetch('/api/research/queue', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, status: 'Archived' }),
      });
      const data = await res.json();
      if (data.success) {
        setItems(prev => prev.filter(i => i.id !== item.id));
      } else {
        setActing(prev => ({ ...prev, [item.id]: 'error' }));
      }
    } catch {
      setActing(prev => ({ ...prev, [item.id]: 'error' }));
    }
  };

  return (
    <div className="rp-body">
      <SelmaSays
        message={items.length > 0
          ? `I have ${items.length} completed package(s) ready for your review.`
          : `The review queue is empty -- nothing to review yet.`
        }
        sub={items.length > 0
          ? `${commandName} will analyze these and prioritize them once online.`
          : "Completed research jobs will appear here once I've finished them."
        }
      />
      {items.length === 0 ? (
        <div className="selma-empty-state">
          <span className="selma-empty-icon">📬</span>
          <p>Completed intelligence packages will appear here for review.</p>
          <p className="selma-empty-sub">Once {commandName} is online, each one is evaluated with a recommended next step.</p>
        </div>
      ) : (
        <div className="selma-job-list">
          {items.map((item, i) => (
            <div key={i} className="review-item">
              <div className="review-item-header">
                <span className="review-item-icon">{item.icon || '📄'}</span>
                <div>
                  <div className="review-item-name">{item.name}</div>
                  <div className="review-item-type">{item.type}</div>
                </div>
                <span className={`review-item-badge review-item-badge--${item.priority?.toLowerCase() || 'medium'}`}>
                  {item.priority || 'Medium'}
                </span>
              </div>
              <p className="review-item-summary">{item.summary}</p>
              <div className="review-item-actions">
                {acting[item.id] === 'done' ? (
                  <span style={{ color: 'var(--dm-green)', fontSize: 13 }}>✅ Approved</span>
                ) : acting[item.id] === 'error' ? (
                  <span style={{ color: 'var(--dm-red)', fontSize: 13 }}>⚠️ Failed — try again</span>
                ) : (
                  <>
                    <button
                      className="rp-btn-process"
                      disabled={!!acting[item.id]}
                      onClick={() => handleApprove(item)}
                    >
                      {acting[item.id] === 'approving' ? 'Saving...' : 'Approve & Save'}
                    </button>
                    <button
                      className="rp-btn-ghost-sm"
                      disabled={!!acting[item.id]}
                      onClick={() => handleArchive(item)}
                    >
                      {acting[item.id] === 'archiving' ? 'Archiving...' : 'Archive'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// SHARED: Job Card
// -----------------------------------------------------------------
// -----------------------------------------------------------------
// ROOT COMPONENT
// -----------------------------------------------------------------

// ════════════════════════════════════════════════════════════════════════════
// INGEST TAB — Contact list ingestion (CSV/Excel → Contacts + Leads)
// ════════════════════════════════════════════════════════════════════════════
function IngestTab() {
  const [stage, setStage]             = useState('upload');   // upload | mapping | preview | committing | done
  const [sourceTag, setSourceTag]     = useState('');
  const [rawRows, setRawRows]         = useState([]);
  const [headers, setHeaders]         = useState([]);
  const [columnMap, setColumnMap]     = useState({});
  const [preview, setPreview]         = useState([]);
  const [counts, setCounts]           = useState({});
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [excluded, setExcluded]       = useState(new Set());
  const [dragOver, setDragOver]       = useState(false);

  const STANDARD_FIELDS = [
    // Contact fields
    'full_name', 'first_name', 'last_name',
    'title', 'department', 'email', 'phone', 'mobile',
    'linkedin', 'notes',
    // Company / account fields
    'company', 'company_hq_street', 'company_hq_city', 'company_hq_state', 'company_hq_zip',
    'company_annual_revenue', 'company_website', 'company_employees', 'company_type',
    // Always last
    'ignore',
  ];

  const FIELD_LABELS = {
    full_name:         'Full Name (contact)',
    first_name:        'First Name',
    last_name:         'Last Name',
    title:             'Job Title',
    department:        'Department',
    email:             'Email',
    phone:             'Phone (direct)',
    mobile:            'Mobile',
    linkedin:          'LinkedIn URL',
    notes:             'Notes',
    company:           'Company Name',
    company_hq_street:     'HQ Street',
    company_hq_city:       'HQ City',
    company_hq_state:      'HQ State',
    company_hq_zip:        'HQ Zip',
    company_annual_revenue:'Annual Revenue',
    company_website:       'Company Website',
    company_employees: 'Employee Count',
    company_type:      'Company Type',
    ignore:            '— ignore —',
  };

  // ── Parse CSV client-side ─────────────────────────────────────────────────
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return { headers: [], rows: [] };
    const hdrs = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
    const rows = lines.slice(1).map(line => {
      const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|^(?=,)|(?<=,)$)/g) || [];
      const row = {};
      hdrs.forEach((h, i) => { row[h] = (vals[i] || '').replace(/^"|"$/g, '').trim(); });
      return row;
    }).filter(r => Object.values(r).some(v => v));
    return { headers: hdrs, rows };
  };

  // P0.30 30.3a — EXCEL, FOR REAL THIS TIME.
  //
  // The picker offered `.csv,.xlsx,.xls` and the handler then answered "Excel
  // support coming soon — please export as CSV for now." A user who selected the
  // file the dialog itself offered was told to go away.
  //
  // WHY read-excel-file AND NOT SheetJS. npm's `xlsx` is stuck at 0.18.5 (2022)
  // because SheetJS moved distribution to its own CDN, and that version carries
  // TWO HIGH-SEVERITY advisories with no npm fix: Prototype Pollution
  // (GHSA-4r6h-8v6p-xvw6) and ReDoS (GHSA-5pgg-2g8v-p4x9). This code parses a
  // file an untrusted user uploaded, which is exactly the threat model both
  // describe. exceljs audits at 2 moderate. read-excel-file audits clean (0
  // advisories) and does precisely this one job.
  //
  // `.xls` IS GONE FROM THE PICKER, deliberately. read-excel-file reads `.xlsx`
  // only — the legacy OLE2/BIFF container is explicitly unsupported — so
  // continuing to offer `.xls` would repeat the original sin in a smaller way.
  // Offering only what works is the point of this change.
  const handleFile = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx'].includes(ext)) {
      setError(ext === 'xls'
        ? 'Legacy .xls files are not supported — please re-save as .xlsx or CSV.'
        : 'Please upload a CSV or .xlsx file.');
      return;
    }
    const accept = (hdrs, rows) => {
      if (!hdrs.length) { setError('Could not parse file — check format.'); return; }
      setHeaders(hdrs);
      setRawRows(rows);
      setError(null);
      setStage('mapping');
      runPreview(rows, null);
    };
    if (ext === 'csv') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const { headers: hdrs, rows } = parseCSV(e.target.result);
        accept(hdrs, rows);
      };
      reader.readAsText(file);
    } else {
      // Imported lazily so the parser is not in the initial bundle — an ingest is
      // a deliberate action, and most sessions never reach this line.
      //
      // THE SUBPATH IS REQUIRED. This package publishes NO root export, only
      // ./browser, ./node, ./universal and ./web-worker. Importing the bare name
      // fails the production build with `"." is not exported under the conditions
      // ["import","module","webpack","production","browser"]` — which vitest does
      // NOT catch, because it resolves differently. Caught only by running the
      // build, which is why the build runs first.
      import('read-excel-file/browser').then(({ default: readXlsxFile }) =>
        readXlsxFile(file).then(result => {
          // THE RETURN SHAPE IS NOT ALWAYS ROWS. read-excel-file gives a plain
          // array-of-arrays for a single sheet, but `[{ sheet, data }]` when the
          // workbook has sheet metadata — verified against a real .xlsx, where
          // the first form threw "(sheet[0] || []).map is not a function". Both
          // are unwrapped here; the first sheet wins, which is what a user who
          // uploaded a contact list means.
          const sheet = Array.isArray(result?.[0]?.data) ? result[0].data : result;
          if (!Array.isArray(sheet) || !sheet.length) {
            setError('That spreadsheet has no readable rows on its first sheet.');
            return;
          }
          // Same shape parseCSV returns, so everything downstream is untouched:
          // first row is the header, cells coerced to trimmed strings, and rows
          // that are entirely empty dropped — Excel pads trailing blank rows.
          const hdrs = (sheet[0] || []).map(h => String(h ?? '').trim()).filter(Boolean);
          const rows = sheet.slice(1).map(r => {
            const row = {};
            hdrs.forEach((h, i) => { row[h] = String(r[i] ?? '').trim(); });
            return row;
          }).filter(r => Object.values(r).some(v => v));
          accept(hdrs, rows);
        })
      ).catch(err => {
        console.error('[ingest] xlsx parse failed:', err);
        setError('Could not read that spreadsheet. Re-save it as .xlsx or export CSV.');
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // ── Run preview via API ───────────────────────────────────────────────────
  const runPreview = async (rows, map) => {
    setLoading(true);
    setError(null);
    try {
      const res  = await apiFetch('/api/research/ingest-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: rows || rawRows, sourceTag, columnMap: map || undefined }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setColumnMap(data.suggestedColumnMap || {});
      setPreview(data.preview || []);
      setCounts(data.counts || {});
      setStage('preview');
    } catch (err) {
      setError('Preview failed: ' + err.message);
    }
    setLoading(false);
  };

  // ── Commit ────────────────────────────────────────────────────────────────
  const handleCommit = async () => {
    setStage('committing');
    setLoading(true);
    try {
      const items = preview.map((item, i) => ({ ...item, excluded: excluded.has(i) }));
      const res   = await apiFetch('/api/research/ingest-commit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sourceTag }),
      });
      const data = await res.json();
      setCommitResult(data);
      setStage('done');
    } catch (err) {
      setError('Commit failed: ' + err.message);
      setStage('preview');
    }
    setLoading(false);
  };

  const toggleExclude = (i) => {
    setExcluded(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const reset = () => {
    setStage('upload'); setRawRows([]); setHeaders([]); setColumnMap({});
    setPreview([]); setCounts({}); setExcluded(new Set());
    setCommitResult(null); setError(null); setSourceTag('');
  };

  const ACTION_LABELS = {
    update_contact: { label: 'Update Contact', color: 'var(--dm-blue-vivid)', bg: 'rgba(37,99,235,0.08)' },
    new_contact:    { label: 'New Contact',     color: 'var(--dm-green)', bg: 'rgba(5,150,105,0.08)' },
    new_lead:       { label: 'New Lead',        color: 'var(--dm-amber)', bg: 'rgba(217,119,6,0.08)' },
  };
  const FIT_COLORS = { High: 'var(--dm-green)', Medium: 'var(--dm-amber)', Low: '#64748b' };

  return (
    <div className="selma-ingest">

      {/* ── UPLOAD STAGE ── */}
      {stage === 'upload' && (
        <div className="ingest-upload-stage">
          <div className="ingest-selma-intro">
            <div className="ingest-selma-icon">🔬</div>
            <div className="ingest-selma-text">
              <strong>Selma here.</strong> Drop a contact list and I'll match everyone against your accounts,
              flag who's already in DM, score the new faces for fit against your market, and set up Lead Cards for the rest.
              Nothing gets written until you approve it.
            </div>
          </div>

          <div className="ingest-source-row">
            <label className="ingest-source-label">Source / Event Tag</label>
            <input
              className="ingest-source-input"
              placeholder="e.g. Industry Summit 2026, Vendor Conference, LinkedIn Export"
              value={sourceTag}
              onChange={e => setSourceTag(e.target.value)}
            />
          </div>

          <div
            className={`ingest-dropzone${dragOver ? ' drag-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('ingest-file-input').click()}>
            <div className="ingest-drop-icon">📁</div>
            <div className="ingest-drop-title">Drop your CSV here</div>
            <div className="ingest-drop-sub">or click to browse — CSV files supported</div>
            <input
              id="ingest-file-input"
              type="file"
              accept=".csv,.xlsx"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
          </div>
          {error && <div className="ingest-error">{error}</div>}
        </div>
      )}

      {/* ── MAPPING / LOADING STAGE ── */}
      {(stage === 'mapping' || loading) && (
        <div className="ingest-loading">
          <div className="ingest-loading-icon">🔬</div>
          <div className="ingest-loading-text">
            Selma is mapping your columns and matching against your accounts...
          </div>
          <div className="ingest-loading-sub">{rawRows.length} rows detected</div>
        </div>
      )}

      {/* ── PREVIEW STAGE ── */}
      {stage === 'preview' && !loading && (
        <div className="ingest-preview-stage">
          <div className="ingest-preview-header">
            <div className="ingest-preview-title">Selma's Preview</div>
            <div className="ingest-preview-counts">
              {sourceTag && <span className="ingest-tag">{sourceTag}</span>}
              <span className="ingest-count ingest-count--blue">{counts.matched_contact || 0} updates</span>
              <span className="ingest-count ingest-count--green">{counts.new_contact || 0} new contacts</span>
              <span className="ingest-count ingest-count--amber">{counts.new_lead || 0} new leads</span>
              {counts.skipped > 0 && <span className="ingest-count">{counts.skipped} skipped</span>}
            </div>
          </div>

          <div className="ingest-column-map">
            <div className="ingest-map-title">Column mapping — correct if needed:</div>
            <div className="ingest-map-grid">
              {headers.map(h => (
                <div key={h} className="ingest-map-row">
                  <span className="ingest-map-header">{h}</span>
                  <span className="ingest-map-arrow">→</span>
                  <select
                    className="ingest-map-select"
                    value={columnMap[h] || 'ignore'}
                    onChange={e => setColumnMap(prev => ({ ...prev, [h]: e.target.value }))}>
                    {STANDARD_FIELDS.map(f => <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button className="ingest-remap-btn" onClick={() => runPreview(rawRows, columnMap)}>
              ↺ Re-run with updated mapping
            </button>
          </div>

          <div className="ingest-commit-bar">
            <div className="ingest-commit-top-row">
              <span className="ingest-commit-summary">
                {preview.length - excluded.size} of {preview.length} rows will be committed
                {excluded.size > 0 && <span className="ingest-skipped-note"> · {excluded.size} skipped</span>}
              </span>
            </div>
            <div className="ingest-commit-btn-row">
              <button className="ingest-cancel-btn" onClick={reset}>Cancel</button>
              <button className="ingest-commit-btn" onClick={handleCommit} disabled={preview.length === excluded.size}>
                Commit Data
              </button>
            </div>
          </div>

          <div className="ingest-preview-list">
            {preview.map((item, i) => {
              const actionCfg = ACTION_LABELS[item.action] || ACTION_LABELS.new_lead;
              const isExcluded = excluded.has(i);
              return (
                <div key={i} className={`ingest-preview-row${isExcluded ? ' excluded' : ''}`}>
                  <div className="ingest-preview-main">
                    <div className="ingest-preview-name">{item.fullName || '—'}</div>
                    <div className="ingest-preview-meta">
                      {item.title && <span>{item.title}</span>}
                      {item.company && <span>· {item.company}</span>}
                      {item.email && <span>· {item.email}</span>}
                    </div>
                    {item.accountMatch && (
                      <div className="ingest-preview-match">
                        Matched: <strong>{item.accountMatch.name}</strong>
                        {item.accountMatch.tier && ` (${item.accountMatch.tier})`}
                      </div>
                    )}
                  </div>
                  <div className="ingest-preview-badges">
                    <span className="ingest-action-badge" style={{ color: actionCfg.color, background: actionCfg.bg }}>
                      {actionCfg.label}
                    </span>
                    <span className="ingest-fit-badge" style={{ color: FIT_COLORS[item.fitScore] }}>
                      {item.fitScore} fit
                    </span>
                  </div>
                  <button
                    className={`ingest-exclude-btn${isExcluded ? ' excluded' : ''}`}
                    onClick={() => toggleExclude(i)}
                    title={isExcluded ? 'Include' : 'Exclude'}>
                    {isExcluded ? '+ Include' : '✕ Skip'}
                  </button>
                </div>
              );
            })}
          </div>

          {error && <div className="ingest-error">{error}</div>}
        </div>
      )}

      {/* ── COMMITTING STAGE ── */}
      {stage === 'committing' && (
        <div className="ingest-loading">
          <div className="ingest-loading-icon">💾</div>
          <div className="ingest-loading-text">Writing to DM...</div>
        </div>
      )}

      {/* ── DONE STAGE ── */}
      {stage === 'done' && commitResult && (
        <div className="ingest-done">
          <div className="ingest-done-icon">✅</div>
          <div className="ingest-done-title">Ingest complete</div>
          <div className="ingest-done-counts">
            <div className="ingest-done-stat">
              <span className="ingest-done-num">{commitResult.created_contacts}</span>
              <span>contacts created</span>
            </div>
            <div className="ingest-done-stat">
              <span className="ingest-done-num">{commitResult.updated_contacts}</span>
              <span>contacts updated</span>
            </div>
            <div className="ingest-done-stat">
              <span className="ingest-done-num">{commitResult.created_leads}</span>
              <span>lead cards created</span>
            </div>
          </div>
          {commitResult.errors?.length > 0 && (
            <div className="ingest-done-errors">
              {commitResult.errors.length} rows had errors — check console for details.
            </div>
          )}
          <button className="ingest-commit-btn" onClick={reset}>Ingest Another List</button>
        </div>
      )}
    </div>
  );
}

export default function ResearchPage({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('quick');
  const [selmaStatus, setSelmaStatus] = useState('Standing By');

  const handleJobQueued = (job) => {
    if (!job.runNow) { setActiveTab('overnight'); setSelmaStatus('Intel Waiting'); }
    else { setActiveTab('queue'); setSelmaStatus('Working'); }
  };

  return (
    <div className="rp-root">
      <SelmaHeader activeTab={activeTab} onTabChange={setActiveTab} selmaStatus={selmaStatus} />
      {activeTab === 'quick'     && <QuickResearchTab onNavigate={onNavigate} onStatusChange={setSelmaStatus} />}
      {activeTab === 'assign'    && <AssignWorkTab onJobQueued={handleJobQueued} />}
      {activeTab === 'queue'     && <ActiveQueueTab />}
      {activeTab === 'overnight' && <OvernightBatchTab />}
      {activeTab === 'review'    && <ReviewQueueTab />}
      {activeTab === 'ingest'    && <IngestTab />}
    </div>
  );
}

// -----------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------
// B45 — formatActionsForEdit() and parseExecutives() lived here until 12 Sep 2026.
// Both existed only to shape an inline research result into the editable form, and
// that flow is gone: research is queued now, and the finished report is read back
// from raw_intelligence by the account surfaces rather than edited here.
//
// One lesson from parseExecutives is worth keeping even though the code is not. Its
// name-matching regex deliberately EXCLUDED the period: sentence fragments such as
// "In role unknown. CPA Canada designation" were matching as names and creating
// phantom contacts. The cost was that honorifics ("Jr.", "John W.") fell through
// into the previous executive's background. If executive parsing is ever rebuilt,
// that tradeoff is the thing to get right first.
